import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaRegStar, FaStar } from 'react-icons/fa';
import './ReviewDialog.css';

const ReviewDialog = ({
    open,
    sellerName,
    itemTitle,
    busy = false,
    initialRating = 5,
    initialReviewText = '',
    isEditing = false,
    onConfirm,
    onCancel,
}) => {
    const [rating, setRating] = useState(initialRating);
    const [reviewText, setReviewText] = useState(initialReviewText);
    const [hoverRating, setHoverRating] = useState(0);

    useEffect(() => {
        if (!open) return;
        setRating(initialRating);
        setReviewText(initialReviewText);
        setHoverRating(0);
    }, [open, initialRating, initialReviewText]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !busy) onCancel?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, busy, onCancel]);

    if (!open) return null;

    const handleSubmit = () => {
        onConfirm?.({ rating, reviewText: reviewText.trim() });
    };

    const selectRating = (star) => {
        if (busy) return;
        setRating(star);
        setHoverRating(star);
    };

    const displayRating = hoverRating || rating;

    return createPortal(
        <div className="msv-review-overlay" role="presentation" onClick={() => !busy && onCancel?.()}>
            <div
                className="msv-review-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="msv-review-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="msv-review-icon" aria-hidden="true">
                    <FaStar />
                </div>
                <h3 id="msv-review-title">{isEditing ? 'Edit your review' : 'Leave a review'}</h3>
                <p className="msv-review-intro">
                    {sellerName
                        ? `Share your experience with ${sellerName}`
                        : 'Share your experience with this seller'}
                    {itemTitle ? ` for "${itemTitle}".` : '.'}
                </p>

                <div className="msv-review-fields">
                    <span className="msv-review-field-label" id="msv-review-rating-label">
                        Rating
                    </span>
                    <div
                        className="msv-review-stars"
                        role="radiogroup"
                        aria-labelledby="msv-review-rating-label"
                        onMouseLeave={() => !busy && setHoverRating(0)}
                    >
                        {[1, 2, 3, 4, 5].map((star) => {
                            const filled = star <= displayRating;
                            return (
                                <button
                                    key={star}
                                    type="button"
                                    className={`msv-review-star${filled ? ' is-filled' : ''}`}
                                    role="radio"
                                    aria-checked={rating === star}
                                    aria-label={`${star} star${star === 1 ? '' : 's'}`}
                                    disabled={busy}
                                    tabIndex={rating === star ? 0 : -1}
                                    onMouseEnter={() => !busy && setHoverRating(star)}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        selectRating(star);
                                    }}
                                    onKeyDown={(event) => {
                                        if (busy) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            selectRating(star);
                                        }
                                    }}
                                >
                                    <span className="msv-review-star-icon" aria-hidden="true">
                                        {filled ? <FaStar /> : <FaRegStar />}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="msv-review-rating-hint">
                        {rating} star{rating === 1 ? '' : 's'} selected
                    </p>

                    <label htmlFor="msv-review-text">Your review</label>
                    <textarea
                        id="msv-review-text"
                        placeholder="Share your experience..."
                        value={reviewText}
                        disabled={busy}
                        onChange={(event) => setReviewText(event.target.value)}
                    />
                </div>

                <div className="msv-review-actions">
                    <button
                        type="button"
                        className="msv-review-cancel"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="msv-review-submit"
                        onClick={handleSubmit}
                        disabled={busy}
                    >
                        {busy ? 'Submitting…' : (isEditing ? 'Save changes' : 'Submit review')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ReviewDialog;
